import { motion } from "framer-motion";
import { FileLock2, ShieldCheck, Network, ArrowRight } from "lucide-react";

const SPECS = [
    { k: "Client-Side Encryption", v: "AES-256-GCM", sub: "Keys never leave device" },
    { k: "Erasure Coding", v: "Reed-Solomon 16/10", sub: "10 data + 6 parity fragments" },
    { k: "Reconstruction", v: "Any 10 of 16", sub: "Up to 6 fragments can be lost" },
    { k: "Verification", v: "Proof of Storage", sub: "On-chain anchored proofs" },
    { k: "Global Diversity", v: "Region & Operator", sub: "Geographic + organizational separation" },
    { k: "Settlement", v: "$DCX", sub: "Per-byte, per-epoch on-chain" },
];

export default function Storage() {
    return (
        <section id="storage" className="relative py-24 md:py-32 lg:py-40 border-t border-line overflow-hidden" data-testid="storage-section">
            <div className="absolute inset-0 hero-glow-bg opacity-30 pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                <div className="max-w-3xl mb-16">
                    <div className="overline mb-4">06 — Encrypted Distributed Storage</div>
                    <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl leading-[1.06] tracking-tight text-ink">
                        Encrypted Storage Without<br />
                        <span className="brand-gradient-text">Trusting a Single Provider.</span>
                    </h2>
                    <p className="mt-6 text-base text-muted-ink max-w-2xl leading-relaxed">
                        DeCloudX storage uses client-side encryption and erasure coding to distribute data across independent storage nodes. Files are encrypted before leaving the user device, split into fragments, distributed globally, and verified through on-chain Proof of Storage.
                    </p>
                </div>

                <div className="surface-strong rounded-3xl p-6 md:p-10 mb-12 relative overflow-hidden" data-testid="storage-pipeline">
                    <div className="absolute inset-0 dot-grid opacity-30" />
                    <div className="relative grid lg:grid-cols-12 gap-5 items-center">
                        <PipelineStep
                            n="01" title="Source File" sub="Document · Database · Media · Model Weights"
                            icon={<div className="w-14 h-16 rounded-md border border-line surface flex items-center justify-center">
                                <div className="space-y-1 px-2 w-full">
                                    {[0,1,2,3].map(i => <div key={i} className="h-0.5 rounded bg-muted-ink/40" />)}
                                </div>
                            </div>}
                            col="lg:col-span-2"
                        />
                        <Arrow />
                        <PipelineStep
                            n="02" title="AES-256-GCM" sub="Encrypted before leaving device"
                            icon={<div className="w-14 h-16 rounded-md border border-brand-cyan/40 bg-gradient-to-b from-brand-cyan/10 to-transparent flex items-center justify-center">
                                <FileLock2 size={20} className="text-brand-cyan" strokeWidth={1.5} />
                            </div>}
                            col="lg:col-span-2"
                        />
                        <Arrow />
                        <PipelineStep
                            n="03" title="RS 16/10" sub="10 data + 6 parity fragments"
                            icon={<div className="grid grid-cols-4 gap-1 w-16">
                                {Array.from({ length: 16 }).map((_, i) => (
                                    <div key={i} className={`aspect-square rounded-sm ${i < 10 ? "bg-gradient-to-br from-brand-violet to-brand-blue" : "bg-brand-cyan/30 border border-brand-cyan/40"}`} />
                                ))}
                            </div>}
                            col="lg:col-span-2"
                        />
                        <Arrow />
                        <PipelineStep
                            n="04" title="Global Nodes" sub="Distributed across regions & operators"
                            icon={<div className="relative w-16 h-16">
                                {[{ t: "8%", l: "12%" },{ t: "30%", l: "70%" },{ t: "55%", l: "20%" },{ t: "75%", l: "60%" },{ t: "20%", l: "45%" }].map((p, i) => (
                                    <div key={i} className="absolute w-2 h-2 rounded-full bg-brand-cyan animate-pulse" style={{ top: p.t, left: p.l, boxShadow: '0 0 6px #06B6D4' }} />
                                ))}
                                <Network size={18} className="absolute inset-0 m-auto text-faint-ink" strokeWidth={1.5} />
                            </div>}
                            col="lg:col-span-2"
                        />
                    </div>

                    <div className="relative mt-8 pt-6 border-t border-line flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                                <ShieldCheck size={16} className="text-emerald-600 dark:text-emerald-400" strokeWidth={1.5} />
                            </div>
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-400">Proof of Storage</div>
                                <div className="text-sm text-ink-2">Continuously verified · anchored on Layer-1</div>
                            </div>
                        </div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">
                            Reconstruct from any 10 of 16 · Resilient to 6 fragment loss
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {SPECS.map((s, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.04 }}
                            className="surface card-lift p-5 rounded-xl"
                            data-testid={`storage-spec-${i}`}
                        >
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">{s.k}</div>
                            <div className="font-heading font-bold text-lg text-ink mt-1.5">{s.v}</div>
                            <div className="text-xs text-muted-ink mt-1.5">{s.sub}</div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Arrow() {
    return (
        <div className="lg:col-span-1 flex justify-center">
            <div className="hidden lg:flex w-full h-px bg-gradient-to-r from-transparent via-line-strong to-transparent relative items-center">
                <ArrowRight size={14} className="text-faint-ink absolute -right-2 -top-2" />
            </div>
        </div>
    );
}

function PipelineStep({ n, title, sub, icon, col }) {
    return (
        <div className={`${col} text-center`}>
            <div className="flex justify-center mb-3">{icon}</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-faint-ink">{n}</div>
            <div className="font-heading font-bold text-sm text-ink mt-1">{title}</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-faint-ink mt-1.5 leading-relaxed">{sub}</div>
        </div>
    );
}
